import React, { FC, useMemo, useRef } from 'react'
import { Link, useNavigate, type RouteObject } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { toast } from 'sonner'
import './default.css'

// Capacitor
import { Share } from '@capacitor/share'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'
import UserResourceStatusCell from '../_components/user-attribute-status-cell'
import Avatar from '@components/molecules/avatar'
import Card from '@components/atoms/card'
import { platform } from '@utils/capacitor-helper'
import { useShareSheet } from '@components/organisims/share-card'
import { priceFormatter } from '@/utils/number-helper'
import { countSupportedExternalAccounts } from '@utils/oauth'

// UTILITIES
import { useUserReferral } from '@/hooks/tanstack/useUserReferral'


const Component: FC = () => {
    const { isSignedIn, user } = useUser()
    const navigate = useNavigate()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const referralDialogRef = useRef<HTMLDialogElement>(null)
    const { data: referralData } = useUserReferral(user?.id)
    const showCreditAmountReferral = user?.publicMetadata?.referralUser === true
    const connectedAccountsCount = useMemo(() => countSupportedExternalAccounts(user), [user?.externalAccounts])

    const userFullName = useMemo(() => `${user?.firstName} ${user?.lastName}`.trim(), [user?.firstName, user?.lastName])

    function changeProfilePicture() {
        fileInputRef.current?.click()
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { name, files } = e.currentTarget
        if (name === 'file-input' && files && files[0]) updateUserProfileImage(files[0])
    }

    function updateUserProfileImage(image: File) {
        if (!isSignedIn || !user.id) return;
        user?.setProfileImage({ file: image })
            .catch((err) => { console.error(err) })
    }

    const referralUrl = useMemo(() => {
        if (!referralData || referralData.status !== 'successful' || !referralData.referral?.id) return ''
        const baseUrl = import.meta.env.DEV
            ? 'https://dev.group.splitcard.com'
            : 'https://group.splitcard.com'
        return `${baseUrl}/referral/${referralData.referral.id}`
    }, [referralData])
    const { canShare, share } = useShareSheet({ url: referralUrl })

    function handleShareReferralLink() {
        if (platform === 'web') {
            if (canShare) share();
            else window.navigator.clipboard.writeText(referralUrl)
                .then(() => toast.success('Link Copied'))
                .catch(() => toast.error('Failed to copy link'))
        } else {
            Share.canShare()
                .then(result => {
                    if (result.value) Share.share({
                        url: referralUrl,
                        title: 'Join me on Splitcard!'
                    })
                })
        }
    }

    function openReferralDialog() {
        if (referralDialogRef.current) referralDialogRef.current.showModal()
    }

    function closeReferralDialog() {
        if (referralDialogRef.current) referralDialogRef.current.close()
    }

    return (<>
        <h1>Profile</h1>
        <div className="user-details flex flex-row items-center">
            <Avatar user={user} />
            <h4 className='flex-1'>{userFullName}</h4>
            <a className='text-blue-500' onClick={changeProfilePicture}>Change Image</a>
            <input ref={fileInputRef} type="file" name="file-input" accept="image/png, image/jpeg" onChange={handleChange} hidden />
        </div>

        {/* Referral Balance Section */}
        <div style={{
            background: 'linear-gradient(135deg, #20c997 0%, #17a2b8 100%)',
            borderRadius: 'var(--border-radius-lrg)',
            padding: '1.3rem',
            color: 'var(--splitcard-white)',
        }}>
            <div className="flex flex-row items-stretch justify-between">
                <div className="flex flex-col justify-between gap-8 flex-1">
                    <div className='flex flex-col gap-4'>
                        <div className='flex flex-row items-center gap-2'>
                            <img src="/splitcard/symbol/white.svg" alt="Splitcard Symbol" className='w-5 h-5' />
                            <p className="text-body-2"><strong>CREDIT</strong></p>
                        </div>
                        <h2 className='text-[2.5rem] font-bold'>{priceFormatter.format((user?.publicMetadata?.creditAmount || 0) / 100)}</h2>
                    </div>
                    <p className="text-body-2">You can apply it when creating any new group.</p>
                </div>
                <div className="bubble-xxsml flex items-center justify-center !bg-white/20 !rounded-[5px]">
                    <Icons.GiftBox width="1.5rem" height="1.5rem" />
                </div>
            </div>
        </div>

        {showCreditAmountReferral && (<>
            {/* Share Referral Link Section */}
            <Card className='gap-3'>
                <div className="flex flex-row items-center gap-2.5">
                    <h4 className='m-0'>Share your referral link</h4>
                    <Icons.Info tint="var(--splitcard-blue-500)" style={{ cursor: 'pointer' }} width="1rem" height="1rem" onClick={openReferralDialog} />
                </div>
                <button
                    onClick={handleShareReferralLink}
                    style={{
                        width: '100%',
                        background: 'var(--splitcard-black)',
                        color: 'var(--splitcard-white)',
                        border: 'none',
                        padding: '1rem',
                        borderRadius: 'var(--border-radius-lrg)',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem'
                    }}
                >
                    <Icons.Share width="1.25rem" height="1.25rem" />
                    Share Link
                </button>
            </Card>

            {/* Referral Dialog */}
            <dialog ref={referralDialogRef}>
                <div>
                    <div style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 4rem)', background: 'var(--splitcard-white)', margin: '1rem', borderRadius: 'var(--border-radius-lrg)', padding: '2rem', textAlign: 'center', position: 'relative', overflowY: 'auto' }}>
                        <button
                            onClick={closeReferralDialog}
                            style={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            aria-label="Close dialog"
                        >
                            <Icons.Close width="1.25rem" height="1.25rem" tint="var(--splitcard-gray-500)" />
                        </button>

                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #20c997 0%, #17a2b8 100%)',
                                borderRadius: '1rem',
                                padding: '1.25rem',
                                display: 'inline-flex'
                            }}>
                                <Icons.GiftBox width="2.5rem" height="2.5rem" tint="white" />
                            </div>
                        </div>

                        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem' }}>Give $5, Get $5</h2>
                        <p style={{ color: 'var(--splitcard-gray-600)', marginBottom: '1rem', fontSize: '0.875rem' }}>
                            Anyone you refer that creates and finalizes a group earns you both <strong style={{ color: 'var(--splitcard-green-600)' }}>$5 credit</strong>
                        </p>

                        <ol style={{
                            listStyle: 'none',
                            margin: 0,
                            padding: 0,
                            textAlign: 'left',
                            marginBottom: '2rem',
                            display: 'grid',
                            gridAutoRows: '1fr',
                            gap: '0.5rem',
                            '--badge-size': '2.5rem',
                            '--line-width': '2px'
                        } as React.CSSProperties}>
                            <li style={{
                                display: 'grid',
                                gridTemplateColumns: 'var(--badge-size) 1fr',
                                columnGap: '1rem',
                                position: 'relative',
                                alignItems: 'start'
                            }}>
                                <div style={{
                                    background: '#EBF5FF',
                                    borderRadius: '0.5rem',
                                    padding: '0.75rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '2.5rem',
                                    width: '2.5rem',
                                    height: '2.5rem',
                                    position: 'relative',
                                    zIndex: 2,
                                    border: '1px solid #BFDBFE'
                                }}>
                                    <Icons.AddUser width="1.25rem" height="1.25rem" tint="#3B82F6" />
                                    <div style={{
                                        content: '""',
                                        position: 'absolute',
                                        left: 'calc(50% - 1px)',
                                        top: '100%',
                                        bottom: 'calc(-100% - 0.5rem)',
                                        width: '2px',
                                        background: 'var(--splitcard-gray-100)',
                                        zIndex: 0
                                    }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem' }}>Invite Someone New</h4>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--splitcard-gray-600)', margin: 0 }}>Send your unique referral link to anyone.</p>
                                </div>
                            </li>

                            <li style={{
                                display: 'grid',
                                gridTemplateColumns: 'var(--badge-size) 1fr',
                                columnGap: '1rem',
                                position: 'relative',
                                alignItems: 'start'
                            }}>
                                <div style={{
                                    background: '#F3E8FF',
                                    borderRadius: '0.5rem',
                                    padding: '0.75rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '2.5rem',
                                    width: '2.5rem',
                                    height: '2.5rem',
                                    position: 'relative',
                                    zIndex: 2,
                                    border: '1px solid #E9D5FF'
                                }}>
                                    <Icons.Group width="1.25rem" height="1.25rem" tint="#A855F7" />
                                    <div style={{
                                        content: '""',
                                        position: 'absolute',
                                        left: 'calc(50% - 1px)',
                                        top: '100%',
                                        bottom: 'calc(-100% - 0.5rem)',
                                        width: '2px',
                                        background: 'var(--splitcard-gray-100)',
                                        zIndex: 0
                                    }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem' }}>They Create a Group</h4>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--splitcard-gray-600)', margin: 0 }}>Your friend creates a group with at least 2 participants and spends $10 or more.</p>
                                </div>
                            </li>

                            <li style={{
                                display: 'grid',
                                gridTemplateColumns: 'var(--badge-size) 1fr',
                                columnGap: '1rem',
                                position: 'relative',
                                alignItems: 'start'
                            }}>
                                <div style={{
                                    background: '#D1FAE5',
                                    borderRadius: '0.5rem',
                                    padding: '0.75rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '2.5rem',
                                    width: '2.5rem',
                                    height: '2.5rem',
                                    position: 'relative',
                                    zIndex: 2,
                                    border: '1px solid #86EFAC'
                                }}>
                                    <Icons.Dollar width="1.25rem" height="1.25rem" tint="#10B981" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.25rem' }}>You Both Earn $5</h4>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--splitcard-gray-600)', margin: 0 }}>Credit applied for future use to both your accounts once their group is finalized.</p>
                                </div>
                            </li>
                        </ol>

                        <button
                            onClick={handleShareReferralLink}
                            style={{
                                width: '100%',
                                background: 'var(--splitcard-black)',
                                color: 'var(--splitcard-white)',
                                border: 'none',
                                padding: '1rem',
                                borderRadius: 'var(--border-radius-lrg)',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                marginBottom: '0.75rem'
                            }}
                        >
                            <Icons.Share width="1.25rem" height="1.25rem" />
                            Share Link
                        </button>

                        {/* <p className='text-body-2 text-gray-500 text-center'>Rules and eligibility</p> */}
                    </div>
                </div>
            </dialog>
        </>)}

        <Card className='gap-2'>
            <div className="flex flex-row items-center justify-between">
                <h4>E-mail Address</h4>
                {(!user?.emailAddresses || user?.emailAddresses.length === 0)
                    ? <Link className='text-blue-500' to="./email/">Add</Link>
                    : <Link className='text-blue-500' to={`./email/${user?.emailAddresses?.at(0)?.id || ''}/update`}>Update</Link>
                }
            </div>
            {(!!user?.emailAddresses.length) && user.emailAddresses.map((email, index, arr) => (
                <React.Fragment key={`${email.id}-${index}`}>
                    <UserResourceStatusCell resource={email} />
                    {(index !== arr.length - 1) && <hr />}
                </React.Fragment>
            ))}
        </Card>
        <Card className='gap-2'>
            <div className="flex flex-row items-center justify-between">
                <h4>Phone Number</h4>
                {(!user?.phoneNumbers || user.phoneNumbers.length === 0) && (
                    <Link className='text-blue-500' to="./phone/">Add</Link>
                )}
            </div>
            {(user?.phoneNumbers.length) ? user.phoneNumbers.map((phoneNumber, index, arr) => (
                <React.Fragment key={`${phoneNumber.id}-${index}`}>
                    <UserResourceStatusCell resource={phoneNumber} />
                    {(index !== arr.length - 1) && <hr />}
                </React.Fragment>
            )) : (<h3>Empty</h3>)}
        </Card>
        {(user?.phoneNumbers.length) ? (
            <Card onClick={() => navigate('./sms-settings')} style={{ cursor: 'pointer' }}>
                <div className='flex flex-row items-center justify-between'>
                    <div className='flex flex-col'>
                        <h4>Notification Settings</h4>
                        <p>Text/SMS and Push</p>
                    </div>
                    <Icons.Chevron width="1.25rem" height="1.25rem" facing="right" />
                </div>
            </Card>
        ) : (
            <Card>
                <div className='flex flex-row items-center justify-between'>
                    <div className='flex flex-col'>
                        <h4>Notification Settings</h4>
                        <p>Text/SMS and Push</p>
                    </div>
                    <h3>Empty</h3>
                </div>
            </Card>
        )}
        <Card onClick={() => navigate('./connected-accounts')} style={{ cursor: 'pointer' }}>
            <div className='flex flex-row items-center justify-between'>
                <div className='flex flex-col'>
                    <h4>Connected Accounts</h4>
                    <p className={connectedAccountsCount > 0 ? 'text-green-600 font-bold' : undefined}>
                        {connectedAccountsCount > 0 ? `${connectedAccountsCount} account${connectedAccountsCount === 1 ? '' : 's'} connected` : 'No accounts connected'}
                    </p>
                </div>
                <Icons.Chevron width="1.25rem" height="1.25rem" facing="right" />
            </div>
        </Card>
        {/* <button
            type='button'
            onClick={() => navigate('./delete-account')}
            style={{
                marginTop: '0.5rem',
                alignSelf: 'center',
                background: 'none',
                border: 'none',
                color: 'var(--splitcard-red-600)',
                fontWeight: 700,
                cursor: 'pointer',
                padding: '0.5rem'
            }}
        >
            Delete Account
        </button> */}
        <Column.Footer hidden />
    </>)
}

const DefaultRoute: RouteObject = {
    index: true,
    Component,
}

export { DefaultRoute }
